import React, { useState } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import ToggleSwitch from '../WidgetEditComponents/ToggleSwitch';
import LayoutSelector from '../WidgetEditComponents/LayoutSelector';
import BulletPointsManagement from '../WidgetEditComponents/BulletPointsManagement';
import ImageTooBigPopup from '../WidgetEditComponents/ImageTooBigPopup';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function BulletPointsWidgetEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    console.log('🔍 BulletPointsWidgetEdit rendered:', {
        widgetId: widget.id,
        widgetType: widget.type,
        currentBulletLayout: editData.bulletLayout,
        hasHandleInputChange: !!handleInputChange,
        bulletPointsCount: editData.bulletPoints?.length || 0
    });
    
    // Layout Change Handler
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log('🔍 BulletPoints layout change:', { layoutId, layoutConfig });
        
        // Wende alle Layout-Config-Werte auf einmal an
        if (layoutConfig) {
            console.log('🔍 Applying layout config:', layoutConfig);
            
            // Alle Werte auf einmal in editData setzen und direkt updaten
            const updatedEditData = { ...editData, ...layoutConfig };
            onUpdate(updatedEditData);
        }
    };

    // Upload States
    const [uploadingStates, setUploadingStates] = useState({});
    const [iconPickerStates, setIconPickerStates] = useState({});
    const [isUploadingBackground, setIsUploadingBackground] = useState(false);
    const [showImageTooBigPopup, setShowImageTooBigPopup] = useState(false);
    const [tooBigFileInfo, setTooBigFileInfo] = useState({ size: 0, name: '' });
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);
    
    const setUploadingState = (index, isUploading) => {
        setUploadingStates(prev => ({
            ...prev,
            [index]: isUploading
        }));
    };

    const setIconPickerState = (index, isOpen) => {
        setIconPickerStates(prev => ({
            ...prev,
            [index]: isOpen
        }));
    };

    // Bullet Points verwalten
    const bulletPoints = editData.bulletPoints || [];
    
    const addBulletPoint = () => {
        const newBulletPoints = [...bulletPoints, {
            id: `bp${Date.now()}`,
            richText: '<strong>New Benefit</strong> - Describe your key advantage here...',
            icon: '/img/icons/handdrawn/star.svg',
            iconType: 'picker',
            customIcon: ''
        }];
        handleInputChange({ target: { name: 'bulletPoints', value: newBulletPoints } });
    };

    const removeBulletPoint = (index) => {
        const newBulletPoints = bulletPoints.filter((_, i) => i !== index);
        handleInputChange({ target: { name: 'bulletPoints', value: newBulletPoints } });
    };

    const updateBulletPoint = (index, field, value) => {
        const newBulletPoints = [...bulletPoints];
        newBulletPoints[index] = { ...newBulletPoints[index], [field]: value };
        handleInputChange({ target: { name: 'bulletPoints', value: newBulletPoints } });
    };

    // Icon Upload Handler
    const handleBulletIconUpload = async (index, file) => {
        if (!file) return;
        
        if (file.size > 2 * 1024 * 1024) {
            setTooBigFileInfo({ size: file.size, name: file.name });
            setShowImageTooBigPopup(true);
            return;
        }
        
        setUploadingState(index, true);
        try {
            const uploadedUrl = await uploadImage(file, `bullet-icon-${index}`);
            updateBulletPoint(index, 'customIcon', uploadedUrl);
            updateBulletPoint(index, 'iconType', 'upload');
            console.log('✅ Bullet icon uploaded:', uploadedUrl);
        } catch (error) {
            console.error('❌ Error uploading bullet icon:', error);
            alert('Upload failed. Please try again.');
        } finally {
            setUploadingState(index, false);
        }
    };

    // Icon Picker Handler
    const handleIconSelect = (index, selectedIcon) => {
        if (selectedIcon) {
            updateBulletPoint(index, 'icon', selectedIcon.src);
            updateBulletPoint(index, 'iconType', 'picker');
            updateBulletPoint(index, 'customIcon', ''); // Clear custom icon
        }
        setIconPickerState(index, false);
    };


    // Text Input Configurations
    const textConfig = [
        {
            fieldName: 'titleText',
            label: 'H2 - Main Title',
            type: 'input',
            placeholder: 'Key Benefits',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        }
    ];

    // Color Configuration für MoreSettingsSection
    const colorConfig = [
        [
            {
                fieldName: 'titleColor',
                label: 'Title',
                defaultValue: '#000000',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'bulletPointColor',
                label: 'Bullet Text',
                defaultValue: '#333333',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#ffffff',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'bulletIconColor',
                label: 'Bullet Icon',
                defaultValue: '#667eea',
                icon: '/img/icons.svg'
            },
            {
                fieldName: 'bulletIconBackgroundColor',
                label: 'Icon Background',
                defaultValue: 'rgba(102, 126, 234, 0.1)',
                icon: '/img/background.svg'
            }
        ]
    ];

    // Font Settings Configuration für MoreSettingsSection
    const fontConfig = [
        {
            label: 'Title',
            sizeField: 'titleSize',
            weightField: 'titleWeight',
            defaultSize: '36',
            defaultWeight: '700'
        },
        {
            label: 'Bullet Points',
            sizeField: 'bulletPointSize',
            weightField: 'bulletPointWeight',
            defaultSize: '16',
            defaultWeight: '400'
        }
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - Zusammengefasste "vereinzelte" Regler
    const advancedConfig = [
        // Widget-spezifische Dimensions
        {
            fieldName: 'maxWidth',
            label: 'Max Width',
            type: 'number',
            defaultValue: 1000,
            min: 300,
            max: 1400,
            step: 50,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'bulletPointMinHeight',
            label: 'Min Item Height',
            type: 'number',
            defaultValue: 60,
            min: 40,
            max: 120,
            step: 10,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'contentSpacing',
            label: 'Content Spacing',
            type: 'number',
            defaultValue: 40,
            min: 20,
            max: 80,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'bulletPointSpacing',
            label: 'Bullet Point Spacing',
            type: 'slider',
            defaultValue: 24,
            min: 16,
            max: 60,
            step: 4,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#607D8B',
            description: 'Space between bullet point cards'
        },
        {
            fieldName: 'bulletPointPadding',
            label: 'Bullet Point Padding',
            type: 'slider',
            defaultValue: 20,
            min: 12,
            max: 40,
            step: 4,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#795548',
            description: 'Inner padding of bullet point cards'
        },

        // Behavior Group
        {
            fieldName: 'invertIconColors',
            label: 'Invert Icon Colors',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },

        // Grid Layout Group
        {
            fieldName: 'bulletPointsPerRow',
            label: 'Bullet Points per Row',
            type: 'slider',
            defaultValue: 2,
            min: 1,
            max: 4,
            step: 1,
            unit: '',
            icon: '/img/element.svg',
            color: '#4CAF50',
            description: 'Number of bullet points per row on desktop'
        },
        {
            fieldName: 'bulletPointsPerRowMobile',
            label: 'Bullet Points per Row (Mobile)',
            type: 'slider',
            defaultValue: 1,
            min: 1,
            max: 2,
            step: 1,
            unit: '',
            icon: '/img/element.svg',
            color: '#FF5722',
            description: 'Number of bullet points per row on mobile'
        },

        // Display Group
        {
            fieldName: 'borderRadius',
            label: 'Border Radius',
            type: 'number',
            defaultValue: 0,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'backgroundImageOpacity',
            label: 'Background Opacity',
            type: 'number',
            defaultValue: 10,
            min: 5,
            max: 100,
            step: 5,
            unit: '%',
            icon: '/img/background.svg'
        },
        {
            fieldName: 'bulletIconSize',
            label: 'Icon Size',
            type: 'slider',
            defaultValue: 48,
            min: 32,
            max: 80,
            step: 4,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#3F51B5',
            description: 'Size of bullet point icons'
        },
        {
            fieldName: 'bulletIconBorderRadius',
            label: 'Icon Border Radius',
            type: 'slider',
            defaultValue: 12,
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#009688',
            description: 'Border radius of icon containers'
        },
        {
            fieldName: 'bulletIconPadding',
            label: 'Icon Padding',
            type: 'slider',
            defaultValue: 12,
            min: 6,
            max: 24,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#FF9800',
            description: 'Padding inside icon containers'
        },
        {
            fieldName: 'bulletPointMinHeight',
            label: 'Bullet Point Min Height',
            type: 'slider',
            defaultValue: 60,
            min: 40,
            max: 120,
            step: 10,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#9C27B0',
            description: 'Minimum height for each bullet point card'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">

                {/* Bullet Point Layout Selector */}
                <div className="widget-trenner">
                    <h3>Bullet Point Layout</h3>
                    <hr className="hrrrr" />
                </div>
                
                <LayoutSelector
                    widgetType="bulletPointsWidget"
                    currentLayout={editData.bulletLayout || 'icon-left'}
                    selectedLayout={editData.bulletLayout || 'icon-left'}
                    onLayoutChange={handleLayoutChange}
                />

                {/* 🚀 BACKGROUND IMAGE CONTAINER - direkt nach Layouts */}
                <ImageContainer
                    label="Background Image"
                    fieldName="backgroundImage"
                    currentImage={editData.backgroundImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingBackground}
                    setIsUploading={setIsUploadingBackground}
                    showToggle={true}
                    toggleLabel="Show Background Image"
                    toggleFieldName="showBackgroundImage"
                    toggleChecked={editData.showBackgroundImage || false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'size',
                            label: 'Image Opacity',
                            fieldName: 'backgroundImageOpacity',
                            defaultValue: 10,
                            min: 5,
                            max: 100,
                            step: 5,
                            unit: '%',
                            description: 'Opacity of the background image',
                            convertValue: true
                        },
                        {
                            type: 'image-effect',
                            label: 'Image Effect',
                            fieldName: 'imageEffect',
                            defaultValue: 'none'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* REMOVED: All Grid, Bullet Point & Icon Settings moved to Advanced Settings Component */}

                {/* REMOVED: Smart Icon Inversion moved to Advanced Settings Component */}

                {/* Bullet Symbol Selector für Minimal Layout */}
                {editData.bulletLayout === 'minimal' && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#495057'
                        }}>
                            Bullet Symbol
                        </label>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '8px',
                            marginBottom: '12px'
                        }}>
                            {['•', '◆', '▶', '★', '✓', '→', '●', '■', '▲', '♦', '✦', '※'].map((symbol) => (
                                <button
                                    key={symbol}
                                    onClick={() => handleInputChange({ target: { name: 'bulletSymbol', value: symbol } })}
                                    style={{
                                        padding: '8px',
                                        border: editData.bulletSymbol === symbol ? '2px solid #667eea' : '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        backgroundColor: editData.bulletSymbol === symbol ? 'rgba(102, 126, 234, 0.1)' : '#ffffff',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: editData.bulletSymbol === symbol ? '#667eea' : '#495057',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '32px'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (editData.bulletSymbol !== symbol) {
                                            e.target.style.borderColor = '#667eea';
                                            e.target.style.backgroundColor = 'rgba(102, 126, 234, 0.05)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (editData.bulletSymbol !== symbol) {
                                            e.target.style.borderColor = '#dee2e6';
                                            e.target.style.backgroundColor = '#ffffff';
                                        }
                                    }}
                                >
                                    {symbol}
                                </button>
                            ))}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                            Choose a symbol for your bullet points
                        </div>
                    </div>
                )}

                {/* REMOVED: Bullet Point Min Height moved to Advanced Settings Component */}


                {/* Bullet Points Management */}
                <BulletPointsManagement 
                    bulletPoints={bulletPoints}
                    addBulletPoint={addBulletPoint}
                    removeBulletPoint={removeBulletPoint}
                    updateBulletPoint={updateBulletPoint}
                    handleBulletIconUpload={handleBulletIconUpload}
                    handleIconSelect={handleIconSelect}
                    uploadingStates={uploadingStates}
                    iconPickerStates={iconPickerStates}
                    setIconPickerState={setIconPickerState}
                    editData={editData}
                />


                {/* 🚀 REVOLUTIONARY TEXT SETTINGS SECTION - NUR TITEL */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={false}
                />

                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard Layout Properties */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={[]} // Keine widget-spezifischen Layout Properties
                />

                {/* 🔧 ADVANCED SETTINGS - Widget-spezifische Properties */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* More Settings (Colors & Fonts only) */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - moved to Advanced Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* CSS Animations */}
                <style>
                    {`
                        @keyframes uploadSpin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}
                </style>

            {/* Image Too Big Popup */}
            <ImageTooBigPopup 
                isOpen={showImageTooBigPopup}
                onClose={() => setShowImageTooBigPopup(false)}
                fileSize={tooBigFileInfo.size}
                fileName={tooBigFileInfo.name}
                maxSize={2}
            />

            {/* 💾 SAVE TEMPLATE POPUP */}
            <SaveTemplatePopup
                isOpen={showSaveTemplatePopup}
                onClose={() => setShowSaveTemplatePopup(false)}
                widgetData={editData}
                originalWidgetType={widget.type}
            />

            </div>
        </div>
    );
}

export default BulletPointsWidgetEdit;
